import { Component, Input, OnDestroy, ViewEncapsulation } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import {
  MatLegacyDialogModule as MatDialogModule,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacySnackBarModule, MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { LocalStorageManager } from '@cutting/main/shared/helper/local-storage-manager.helper';
import {
  CuttingMaterial,
  CuttingPanelListModule,
  CuttingSettings,
  DialogsService,
  ICustomResponse,
  NoDataDisplayComponent,
  PresetSettings,
} from 'cloud-shared-lib';
import { Observable, Subject, takeUntil, tap } from 'rxjs';

import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { MatDividerModule } from '@angular/material/divider';
import { UserSettingsService } from '@cutting/main/shared/services/user-settings.service';
import * as _ from 'lodash';
import { OrderDetailService } from '../../order-detail.service';
import { CopyMaterialsInOrderDialogComponent } from './copy-materials-in-order-dialog/copy-materials-in-order-dialog.component';

@Component({
  templateUrl: './log-materials-dialog.component.html',
  styleUrls: ['./log-materials-dialog.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    AsyncPipe,
    MatIconModule,
    MatDividerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    NoDataDisplayComponent,
    CuttingPanelListModule,
    MatLegacySnackBarModule,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class LogMaterialsDialogComponent implements OnDestroy {
  data$: Observable<ICustomResponse<CuttingMaterial[]>>;
  settings$: Observable<CuttingSettings>;
  materials: CuttingMaterial[] = [];
  materialsOrder: CuttingMaterial[] = [];
  newMaterials: CuttingMaterial[] = [];
  presetSettings: PresetSettings;

  get userId(): number {
    return LocalStorageManager.regData;
  }

  @Input() set logId(val: number) {
    this.data$ = this.orderDetailService.getLogMaterials(val).pipe(tap(res => (this.materials = res.result)));
  }

  unsubscribe$: Subject<void> = new Subject();

  constructor(
    public dialogRef: MatDialogRef<LogMaterialsDialogComponent>,
    private dialogsService: DialogsService,
    private orderDetailService: OrderDetailService,
    public snackBar: MatSnackBar,
    private settingsService: UserSettingsService
  ) {
    this.getCuttingSettings();
    this.getPresetSettings();
  }

  copyMaterialsInOrder(): void {
    const isExistIntersection =
      _.intersectionWith(this.materialsOrder, this.materials, (a, b) => a.inMaterialBaseId === b.inMaterialBaseId)
        .length > 0;

    if (isExistIntersection) {
      const dialogRefCopy = this.dialogsService.open(CopyMaterialsInOrderDialogComponent, {
        width: '700px',
        data: {
          historyMaterials: this.materials,
          materialsOrder: this.materialsOrder,
        },
      });

      dialogRefCopy.afterClosed().subscribe((res: CuttingMaterial[] | undefined) => {
        if (res && res.length > 0) {
          this.dialogRef.close(res);
        }
      });
    } else {
      this.dialogRef.close([...this.materialsOrder, ...this.materials]);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }

  private getPresetSettings(): void {
    this.settingsService
      .getPresetSettings()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(res => (this.presetSettings = res));
  }

  private getCuttingSettings(): void {
    this.settings$ = this.settingsService.getCuttingSettings();
  }
}
